import { useState, useEffect, useCallback } from 'react';
import { useDashboardStore } from '@/stores/useDashboardStore';
import { API_BASE_URL } from '@/utils/constants';
import { getDevHeaders } from '@/services/api';

export interface ServiceListItem {
  id: string;
  name: string;
  specName: string | null;
  displayName: string;
  nodeId: string;
  nodeName: string;
  nodePortfolio: string | null;
  nodeProfile: string | null;
  healthStatus: string | null;
  status: string | null;
  statusReason: string | null;
  hostname: string | null;
  totalRestartCount: number;
  runningContainers: string | null;
  serviceType: string | null;
  startTime: string | null;
  updatedAt: string;
}

export interface ServicePageSummary {
  total: number;
  healthy: number;
  unhealthy: number;
  pending: number;
  terminated: number;
  restarting: number;
  unknown: number;
  noHealthCheck: number;
  servicesPerNode: { nodeName: string; count: number }[];
}

interface UseServicesParams {
  health?: string;
  search?: string;
  nodeId?: string;
  serviceType?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function useServicePageSummary() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [summary, setSummary] = useState<ServicePageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const res = await window.fetch(`${API_BASE_URL}/api/services/summary`, { credentials: 'include', headers: getDevHeaders() });
        if (!res.ok) throw new Error('Failed to fetch service summary');
        const data = await res.json();
        if (!cancelled) {
          setSummary(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch service summary'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { summary, loading, error };
}

export function useServicesList(params: UseServicesParams = {}) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [services, setServices] = useState<ServiceListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const depsKey = JSON.stringify(params);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (params.health) qs.set('health', params.health);
      if (params.search) qs.set('search', params.search);
      if (params.nodeId) qs.set('nodeId', params.nodeId);
      if (params.serviceType) qs.set('serviceType', params.serviceType);
      if (params.page) qs.set('page', String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.sortBy) qs.set('sortBy', params.sortBy);
      if (params.sortOrder) qs.set('sortOrder', params.sortOrder);

      const res = await window.fetch(`${API_BASE_URL}/api/services?${qs}`, { credentials: 'include', headers: getDevHeaders() });
      if (!res.ok) throw new Error('Failed to fetch services');
      const data = await res.json();
      setServices(data.services);
      setTotal(data.pagination.total);
      setTotalPages(data.pagination.totalPages);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch services'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, refreshKey]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return { services, total, totalPages, loading, error };
}
